import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router";
import { createInstance } from "src/utils/axios";
import { useTokenContext } from "src/contexts/tokenContext";
import { APIS } from "src/utils/constants";
import { readItemLink } from "src/utils/itemDeepLink.ts";

/**
 * Shipper Maint, Display Shippers and Shipper Retail were folded into Item
 * Maintenance (2026-10-20). Their addresses keep working: whatever key a link
 * carries is resolved to an item, and the browser lands on that item's page.
 *
 *   a shipper (by RITEM #, UPC, or Sys Cd + whitem)  -> its page
 *   a component's key                                -> the component's page,
 *                                                       whose "In shippers"
 *                                                       answers the question
 *   a description                                    -> the item search
 *   a Shipper Retail store (strNo)                   -> carried as costStore,
 *                                                       for the cost panel
 *
 * The same resolvers the shipper screens used, so QRY-02/03 (shipper wins, a
 * 6-digit UPC is expanded) still hold for an old link.
 */
const ShipperRedirect = () => {
  const { search } = useLocation();
  const { accessToken, setAccessToken } = useTokenContext();
  const axiosInstance = useMemo(
    () => createInstance(accessToken, setAccessToken),
    [accessToken, setAccessToken],
  );
  const [target, setTarget] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const p = new URLSearchParams(search);
    const store = p.get("strNo");
    const withStore = (path: string) =>
      store ? `${path}?costStore=${encodeURIComponent(store)}` : path;

    const desc = p.get("desc")?.trim();
    if (p.get("key") === "desc" && desc) {
      setTarget(`/items/maintenance?q=${encodeURIComponent(desc)}`);
      return;
    }
    const link = readItemLink(search, { acceptRitemOnly: true });
    if (!link) {
      setTarget("/items/maintenance");
      return;
    }
    const byKey =
      link.key === "upc" && link.upc
        ? { upc: Number(link.upc) }
        : link.key === "whitemNo" && link.whitemNo != null
          ? { systemCode: link.sysCd ?? null, whitemNumber: link.whitemNo }
          : link.ritemNo != null
            ? { ritemNumber: link.ritemNo }
            : link.upc
              ? { upc: Number(link.upc) }
              : { systemCode: link.sysCd ?? null, whitemNumber: link.whitemNo ?? null };

    void (async () => {
      try {
        if ("ritemNumber" in byKey && byKey.ritemNumber != null) {
          // A RITEM # is already the page's key, shipper or not.
          setTarget(withStore(`/items/maintenance/${byKey.ritemNumber}`));
          return;
        }
        const r = await axiosInstance.get(APIS.ShipperResolve(byKey));
        if (r.data?.shipperNumber != null) {
          setTarget(withStore(`/items/maintenance/${r.data.shipperNumber}`));
          return;
        }
        const c = await axiosInstance.get(
          APIS.ShipperResolveComponent({
            upc: "upc" in byKey ? byKey.upc : null,
            systemCode: "systemCode" in byKey ? byKey.systemCode : null,
            whitemNumber: "whitemNumber" in byKey ? byKey.whitemNumber : null,
          }),
        );
        if (c.data?.itemNumber != null) {
          setTarget(`/items/maintenance/${c.data.itemNumber}#in-shippers`);
          return;
        }
      } catch {
        /* fall through to the search page */
      }
      const q = "upc" in byKey ? String(byKey.upc) : "";
      setTarget(q ? `/items/maintenance?q=${encodeURIComponent(q)}` : "/items/maintenance");
    })();
  }, [search, axiosInstance]);

  if (target) return <Navigate to={target} replace />;
  return <div style={{ padding: 24 }}>Opening…</div>;
};

export default ShipperRedirect;
